/**
 * Skills library — lists agent-registry skills via agent-custodian MCP proxy route.
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { cpFetch } from '@/lib/cpClient';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Loader2, Search, Sparkles } from 'lucide-react';

type SkillRow = {
  skill_id?: string;
  name?: string;
  description?: string;
  domain?: string;
  tier?: number;
  status?: string;
};

async function fetchSkillsViaCustodian(search: string): Promise<SkillRow[]> {
  const resp = await cpFetch<any>('/api/volcano/execute', {
    method: 'POST',
    body: JSON.stringify({
      agent_id: 'agent-creator',
      workflow: 'default',
      input: {
        action: 'list_skills',
        q: search || undefined,
        limit: 50,
      },
    }),
  });
  const payload = resp?.data ?? resp?.internal_data ?? resp;
  const skills = payload?.skills ?? payload?.items ?? [];
  return Array.isArray(skills) ? skills : [];
}

export function SkillsLibraryPanel({ className = '' }: { className?: string }) {
  const [search, setSearch] = useState('');

  const skillsQuery = useQuery({
    queryKey: ['agents', 'skills', search],
    queryFn: () => fetchSkillsViaCustodian(search),
    retry: false,
    staleTime: 120_000,
  });

  const skills = skillsQuery.data ?? [];
  const unavailable = skillsQuery.isError;

  return (
    <Card className={className}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-violet-600" />
          Skills library
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search skills by name or domain…"
            className="pl-9"
          />
        </div>

        {skillsQuery.isLoading && (
          <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading skills…
          </div>
        )}

        {unavailable && (
          <p className="text-sm text-gray-500">
            Skills catalog unavailable via agent-creator ({(skillsQuery.error as Error)?.message || 'error'}).
            Skill MCP discovery tools were removed (F12). Prefer explicit <code className="text-xs">volcano_config</code> agent authoring until skills are rebuilt.
          </p>
        )}

        {skillsQuery.isSuccess && skills.length === 0 && !unavailable && (
          <p className="text-sm text-gray-500">No skills matched your search.</p>
        )}

        {skills.length > 0 && (
          <div className="max-h-48 overflow-y-auto space-y-2">
            {skills.slice(0, 20).map((skill) => (
              <div
                key={skill.skill_id || skill.name}
                className="rounded-md border border-gray-100 bg-gray-50 px-3 py-2 text-sm"
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-gray-900">{skill.name || skill.skill_id}</span>
                  {skill.domain && (
                    <Badge variant="outline" className="text-[10px]">
                      {skill.domain}
                    </Badge>
                  )}
                  {skill.tier != null && (
                    <Badge variant="secondary" className="text-[10px]">
                      tier {skill.tier}
                    </Badge>
                  )}
                </div>
                {skill.description && (
                  <p className="text-xs text-gray-600 mt-1 line-clamp-2">{skill.description}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
